import React from 'react';
import { Helmet } from 'react-helmet';
const SITE_URL = 'https://medicolegal-psychiatry.co.uk';
const DEFAULT_SOCIAL_IMAGE = 'https://uploadthingy.s3.us-west-1.amazonaws.com/u9Nr778koLscH9uY6kZjEn/01._Hero_Image.png';
const DEFAULT_SOCIAL_ALT = 'Medicolegal Psychiatry, independent psychiatric expert witness reports';
export type JsonLd = Record<string, unknown> | Array<Record<string, unknown>>;
type SEOProps = {
  /** Unique <title> for this page */
  title: string;
  /** Unique meta description for this page */
  description: string;
  /** Route path, e.g. '/about'. Use '/' for the homepage. */
  path: string;
  keywords?: string[];
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article';
  /** Page-specific JSON-LD, in addition to the automatic BreadcrumbList */
  schema?: JsonLd;
  noindex?: boolean;
};

// Breadcrumb names for every route below the homepage.
const breadcrumbLabels: Record<string, string> = {
  '/about': 'About Dr Imtiaz Syed',
  '/services': 'Services',
  '/services/mental-capacity': 'Mental Capacity Assessments',
  '/services/trauma-ptsd': 'PTSD and Trauma Evaluations',
  '/services/neurodevelopmental': 'ADHD and Autism Assessments',
  '/services/forensic-risk': 'Forensic and Risk Assessments',
  '/services/specialist': 'Court and Tribunal Reports',
  '/services/clinical-negligence': 'Clinical Negligence Assessments',
  '/faq': 'FAQs',
  '/contact': 'Contact',
  '/sitemap': 'Sitemap',
  '/policies/privacy': 'Privacy and Cookie Policy',
  '/policies/terms': 'Terms and Conditions',
  '/policies/cancellation': 'Cancellation Policy'
};
function getBreadcrumbSchema(path: string) {
  if (path === '/' || !breadcrumbLabels[path]) return null;
  const items: Array<Record<string, unknown>> = [{
    '@type': 'ListItem',
    position: 1,
    name: 'Home',
    item: SITE_URL
  }];
  // Service detail pages sit below the services index.
  if (path.startsWith('/services/')) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: 'Services',
      item: `${SITE_URL}/services`
    });
  }
  items.push({
    '@type': 'ListItem',
    position: items.length + 1,
    name: breadcrumbLabels[path],
    item: `${SITE_URL}${path}`
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
  };
}
export default function SEO({
  title,
  description,
  path,
  keywords = [],
  image = DEFAULT_SOCIAL_IMAGE,
  imageAlt = DEFAULT_SOCIAL_ALT,
  type = 'website',
  schema,
  noindex = false
}: SEOProps) {
  const canonicalUrl = path === '/' ? SITE_URL : `${SITE_URL}${path}`;
  const breadcrumbSchema = getBreadcrumbSchema(path);
  return <Helmet data-id="element-327" data-mp="3n1rjec714quj">
      <title data-id="element-328" data-mp="3n2ar702ytyuu">{title}</title>
      <meta name="description" content={description} data-id="element-329" data-mp="3n0pzrjby6w17" />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} data-id="element-330" data-mp="2fo7poigbamfu" />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} data-id="element-331" data-mp="2bljbrwcqqpc0" />}
      <link rel="canonical" href={canonicalUrl} data-id="element-332" data-mp="r4trg5klf4dz" />
      {/* Open Graph */}
      <meta property="og:type" content={type} data-id="element-333" data-mp="r2qpqoa2vmtf" />
      <meta property="og:site_name" content="Medicolegal Psychiatry" data-id="element-334" data-mp="v76v8ofs4s84" />
      <meta property="og:locale" content="en_GB" data-id="element-335" data-mp="105i5v1dwf6re" />
      <meta property="og:title" content={title} data-id="element-336" data-mp="103oytkzdikiv" />
      <meta property="og:description" content={description} data-id="element-337" data-mp="14votaxwocl82" />
      <meta property="og:url" content={canonicalUrl} data-id="element-338" data-mp="14x9peryn0c81" />
      <meta property="og:image" content={image} data-id="element-339" data-mp="19vulcf6uw9qo" />
      <meta property="og:image:alt" content={imageAlt} data-id="element-340" data-mp="19x5pwh84lliu" />
      {/* Twitter / X card */}
      <meta name="twitter:card" content="summary_large_image" data-id="element-341" data-mp="1esbcli9cr47z" />
      <meta name="twitter:title" content={title} data-id="element-342" data-mp="1iwstn9obv3f2" />
      <meta name="twitter:description" content={description} data-id="element-343" data-mp="1itww3mge6gr5" />
      <meta name="twitter:image" content={image} data-id="element-344" data-mp="1ntjaw0bvc522" />
      <meta name="twitter:image:alt" content={imageAlt} data-id="element-345" data-mp="3kn4bswzx30dk" />
      {schema && <script type="application/ld+json" data-id="element-346" data-mp="3kp75ps46xdnk">{JSON.stringify(schema)}</script>}
      {breadcrumbSchema && <script type="application/ld+json" data-id="element-347" data-mp="2uc0uu1b24qdu">
          {JSON.stringify(breadcrumbSchema)}
        </script>}
    </Helmet>;
}
export const siteUrl = SITE_URL;